import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { Select, Button, TOption, RadioList } from '@treezor/design-system';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';

import { setBulkCardCreationStep } from '@/store/actions/bulkCardCreations.actions';

import { IDashboardConfigSettingsCardsDesign } from '@/store/models/dashboard-config.model';
import { cardsMediums, isValidCardMedium } from '@/features/cards/store/models/bulk-card.model';
import { BulkCardCreationsActionTypes } from '@/features/cards/store/types/bulk-cards-creation.types';
import { cardTypes } from '@/features/cards/store/models/card.model';

interface ICardPrintForm {
  cardPrint: string;
  medium: number;
}

export const CardProgram = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const config = useDashboardConfig();

  const activeStep = useAppSelector((state) => state.bulkCardCreation.currentStep);
  const bulkCardCreationDraft = useAppSelector((state) => state.bulkCardCreation.bulkCardCreationDraft);

  const designs = config?.settings?.cards?.designs;
  const defaultCardDesign = designs?.[0];
  const defaultMedium = defaultCardDesign?.type === cardTypes.PHYSIC ? 1 : 2;

  const defaultValues: ICardPrintForm = {
    cardPrint: bulkCardCreationDraft?.cardPrint || defaultCardDesign?.value?.toString(),
    medium: bulkCardCreationDraft?.medium || defaultMedium,
  };

  const { watch, handleSubmit, setValue } = useForm<ICardPrintForm>({ defaultValues });
  const cardPrintWatch = watch('cardPrint');
  const mediumWatch = watch('medium');
  const cardType = designs?.find((design) => design.value.toString() === cardPrintWatch)?.type;

  const handleCardPrintChange = useCallback(
    (value) => {
      setValue('cardPrint', value.toString());
      const selectedType = designs?.find((design) => design.value === value)?.type;
      if (selectedType) {
        setValue('medium', selectedType === cardTypes.PHYSIC ? 1 : 2);
      }
    },
    [setValue, designs],
  );

  const onSubmit = (data: ICardPrintForm) => {
    if (isValidCardMedium(data.medium)) {
      dispatch(
        setBulkCardCreationStep(activeStep + 1, {
          ...data,
          cardPrint: data.cardPrint.toString(),
          medium: data.medium,
        }),
      );
    } else {
      dispatch({
        type: BulkCardCreationsActionTypes.CREATE_BULK_CARD_FAIL,
        error: t('pages.administration.bulkCards.cardProgram.errors.invalidMedium'),
        documentation: 'https://docs.treezor.com/guide/cards/creation.html#physical-card-creation',
      });
    }
  };

  return (
    <>
      <h5 className="typo-h5-bold text-secondary">{t('pages.administration.bulkCards.cardProgram.title')}</h5>

      <form className="flex flex-col gap-6 mt-7 w-[250px]" onSubmit={handleSubmit(onSubmit)}>
        <Select<TOption, number>
          label={t('pages.administration.bulkCards.cardProgram.forms.cardProgram')}
          options={designs?.map((design: IDashboardConfigSettingsCardsDesign) => ({
            label: design.label,
            value: design.value,
          }))}
          required
          defaultValue={Number(cardPrintWatch) || defaultCardDesign?.value}
          onChange={handleCardPrintChange}
        />

        <div className="flex flex-col gap-2 text-secondary">
          <span className="typo-body-regular-paragraph">
            {t('pages.administration.bulkCards.cardProgram.programId', { value: cardPrintWatch })}
          </span>
          <span className="typo-body-regular-paragraph">
            {t('pages.administration.bulkCards.cardProgram.programType', {
              type:
                cardType === 'physic'
                  ? t(`pages.administration.bulkCards.cardProgram.${cardsMediums.PHYSICAL.label}`)
                  : undefined,
            })}
          </span>
          {cardType === cardTypes.VIRTUAL && (
            <RadioList
              name="medium"
              options={[
                {
                  label: t(`pages.administration.bulkCards.creationDetails.${cardsMediums.VIRTUAL.label}`),
                  value: cardsMediums.VIRTUAL.value.toString(),
                },
                {
                  label: t(`pages.administration.bulkCards.creationDetails.${cardsMediums.VIRTUAL_CONVERTED.label}`),
                  value: cardsMediums.VIRTUAL_CONVERTED.value.toString(),
                },
              ]}
              onChange={(medium) => setValue('medium', Number(medium))}
              value={mediumWatch.toString()}
              className="pt-2 gap-3"
            />
          )}
        </div>

        <div className="flex items-center justify-center">
          <Button id="next-bulk-cards-print-btn" type="submit">
            {t('pages.administration.bulkCards.next')}
          </Button>
        </div>
      </form>
    </>
  );
};
